import { MedplumClient } from '@medplum/core';
import { Specimen, ServiceRequest } from '@medplum/fhirtypes';

export interface SpecimenWorkflowResult {
  hasExistingSpecimens: boolean;
  specimens: Specimen[];
  shouldShowSelectionModal: boolean;
}

export interface SpecimenSelectionResult {
  useExisting: boolean;
  specimen?: Specimen;
  createNew: boolean;
}

/**
 * Check for existing specimens for a patient that could be used for a new order
 * @param medplum - The Medplum client instance
 * @param patientId - The ID of the patient to check specimens for
 * @returns Promise containing workflow result with existing specimens
 */
export async function checkExistingSpecimens(
  medplum: MedplumClient,
  patientId: string
): Promise<SpecimenWorkflowResult> {
  try {
    const specimens = await medplum.searchResources('Specimen', {
      subject: `Patient/${patientId}`,
      status: 'available', // Only available specimens can be used
      _sort: '-_lastUpdated',
      _count: '50',
    });

    return {
      hasExistingSpecimens: specimens.length > 0,
      specimens,
      shouldShowSelectionModal: specimens.length > 0,
    };
  } catch (error) {
    console.error('Error checking existing specimens:', error);
    return {
      hasExistingSpecimens: false,
      specimens: [],
      shouldShowSelectionModal: false,
    };
  }
}

/**
 * Link an existing specimen to a new order
 * @param medplum - The Medplum client instance
 * @param specimenId - ID of the specimen to link
 * @param serviceRequestId - ID of the service request to link to
 */
export async function linkSpecimenToOrder(
  medplum: MedplumClient,
  specimenId: string,
  serviceRequestId: string
): Promise<void> {
  try {
    // Update specimen to reference the order
    const specimen = await medplum.readResource('Specimen', specimenId);
    const currentRequests = specimen.request || [];
    const orderRef = `ServiceRequest/${serviceRequestId}`;
    if (!currentRequests.some((r) => r.reference === orderRef)) {
      const updatedRequests = [...currentRequests, { reference: orderRef }];
      await medplum.patchResource('Specimen', specimenId, [
        {
          op: currentRequests.length === 0 ? 'add' : 'replace',
          path: '/request',
          value: updatedRequests,
        },
      ]);
    }

    // Update order to reference the specimen
    const serviceRequest = await medplum.readResource(
      'ServiceRequest',
      serviceRequestId
    );
    const currentSpecimens = serviceRequest.specimen || [];
    const specimenRef = `Specimen/${specimenId}`;

    if (!currentSpecimens.some((s) => s.reference === specimenRef)) {
      const updatedSpecimens = [
        ...currentSpecimens,
        {
          reference: specimenRef,
          display: specimen.identifier?.[0]?.value || specimenId,
        },
      ];
      await medplum.patchResource('ServiceRequest', serviceRequestId, [
        {
          op: currentSpecimens.length === 0 ? 'add' : 'replace',
          path: '/specimen',
          value: updatedSpecimens,
        },
      ]);
    }
  } catch (error) {
    console.error('Error linking specimen to order:', error);
    throw error;
  }
}

/**
 * Check if an order already has specimens
 * @param medplum - The Medplum client instance
 * @param serviceRequestId - ID of the service request to check
 * @returns Promise<boolean> indicating if order has specimens
 */
export async function checkOrderHasSpecimens(
  medplum: MedplumClient,
  serviceRequestId: string
): Promise<boolean> {
  try {
    const specimens = await medplum.searchResources('Specimen', {
      request: `ServiceRequest/${serviceRequestId}`,
      _count: '1',
    });
    return specimens.length > 0;
  } catch (error) {
    console.error('Error checking order specimens:', error);
    return false;
  }
}

/**
 * Validate specimen-order compatibility
 * @param specimen - The specimen to validate
 * @param serviceRequest - The service request to validate against
 * @returns Object with validation result and optional error message
 */
export function validateSpecimenOrderCompatibility(
  specimen: Specimen,
  serviceRequest: ServiceRequest
): { isValid: boolean; message?: string } {
  // Check patient match
  if (specimen.subject?.reference !== serviceRequest.subject?.reference) {
    return {
      isValid: false,
      message: 'Specimen and order must belong to the same patient',
    };
  }

  // Check specimen status
  if (specimen.status !== 'available') {
    return {
      isValid: false,
      message: 'Only available specimens can be used for new orders',
    };
  }

  // Check if specimen is already linked to this order
  const isAlreadyLinked = specimen.request?.some(
    (r) => r.reference === `ServiceRequest/${serviceRequest.id}`
  );

  if (isAlreadyLinked) {
    return {
      isValid: false,
      message: 'Specimen is already linked to this order',
    };
  }

  return { isValid: true };
}

/**
 * Get specimen type name for display
 * @param specimen - The specimen to get display name for
 * @returns String representation of the specimen for display purposes
 */
export function getSpecimenDisplayName(specimen: Specimen): string {
  const identifier = specimen.identifier?.[0]?.value;
  const type = specimen.type?.coding?.[0]?.display || specimen.type?.text;

  if (identifier && type) {
    return `${identifier} (${type})`;
  }

  return identifier || type || specimen.id || 'Unknown Specimen';
}

/**
 * Workflow patterns for order-specimen relationships
 */
export enum OrderSpecimenWorkflow {
  /** Creating order with no existing specimens - standard flow */
  NEW_ORDER_NO_SPECIMENS = 'new_order_no_specimens',

  /** Creating order when patient has existing specimens - show selection */
  NEW_ORDER_WITH_EXISTING_SPECIMENS = 'new_order_with_existing_specimens',

  /** Creating specimen from existing order - link automatically */
  NEW_SPECIMEN_FROM_ORDER = 'new_specimen_from_order',

  /** Creating specimen when no orders exist - standard specimen creation */
  NEW_SPECIMEN_NO_ORDERS = 'new_specimen_no_orders',
}

/**
 * Determine the appropriate workflow based on context
 * @param medplum - The Medplum client instance
 * @param context - The workflow context
 * @param context.patientId - ID of the patient (optional)
 * @param context.serviceRequestId - ID of the service request (optional)
 * @param context.isCreatingOrder - Whether we're creating a new order
 * @param context.isCreatingSpecimen - Whether we're creating a new specimen
 * @returns Promise<OrderSpecimenWorkflow> indicating the appropriate workflow
 */
export async function determineWorkflow(
  medplum: MedplumClient,
  context: {
    patientId?: string;
    serviceRequestId?: string;
    isCreatingOrder: boolean;
    isCreatingSpecimen: boolean;
  }
): Promise<OrderSpecimenWorkflow> {
  const { patientId, serviceRequestId, isCreatingOrder, isCreatingSpecimen } =
    context;

  if (isCreatingOrder && patientId) {
    const { hasExistingSpecimens } = await checkExistingSpecimens(
      medplum,
      patientId
    );
    return hasExistingSpecimens
      ? OrderSpecimenWorkflow.NEW_ORDER_WITH_EXISTING_SPECIMENS
      : OrderSpecimenWorkflow.NEW_ORDER_NO_SPECIMENS;
  }

  if (isCreatingSpecimen && serviceRequestId) {
    return OrderSpecimenWorkflow.NEW_SPECIMEN_FROM_ORDER;
  }

  if (isCreatingSpecimen && patientId) {
    return OrderSpecimenWorkflow.NEW_SPECIMEN_NO_ORDERS;
  }

  return OrderSpecimenWorkflow.NEW_ORDER_NO_SPECIMENS;
}
